import { Form, Select } from "antd"
import { useMemo, useState } from "react"

import { CLASS_GROUPS } from "#constants/classOptions"
import {
  CORE_SKILL_LEVEL_OPTIONS,
  CORE_SKILL_LEVELS,
  type CoreSkillLevel,
  DIGITAL_SKILL_LEVEL_OPTIONS,
  DIGITAL_SKILL_LEVELS,
  type DigitalSkillLevel,
  SCULPTURE_SKILL_LEVEL_OPTIONS,
  SCULPTURE_SKILL_LEVELS,
  SculptureSkillLevel,
  sortSkillLevels,
} from "#constants/skillLevelConstants"
import { DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import { ReactChild } from "#customTypes/globalTypes"
import { filterFormOptions } from "#utils/formHelpers"
import { camelToWords } from "#utils/textFormatters"

const SkillLevelDropdown = ({
  label,
  required,
  multiSelect,
  classGroup = CLASS_GROUPS.CORE,
  fieldName,
  requireGroupSelection,
  onChange,
}: {
  label?: ReactChild
  required?: boolean
  multiSelect?: boolean
  classGroup?: CLASS_GROUPS
  fieldName?: string | string[]
  requireGroupSelection?: boolean
  onChange?: (
    value:
      | CoreSkillLevel
      | CoreSkillLevel[]
      | DigitalSkillLevel
      | DigitalSkillLevel[]
      | SculptureSkillLevel
      | SculptureSkillLevel[]
  ) => void
}) => {
  const [skillLevels, setSkillLevels] = useState<
    | CoreSkillLevel
    | CoreSkillLevel[]
    | DigitalSkillLevel
    | DigitalSkillLevel[]
    | SculptureSkillLevel
    | SculptureSkillLevel[]
  >()

  const skillLevelOptions: DropdownOption[] = useMemo(() => {
    return classGroup === CLASS_GROUPS.DIGITAL_ART
      ? DIGITAL_SKILL_LEVEL_OPTIONS
      : classGroup === CLASS_GROUPS.SCULPTURE
        ? SCULPTURE_SKILL_LEVEL_OPTIONS
        : CORE_SKILL_LEVEL_OPTIONS
  }, [classGroup])

  // How to handle singular/plural case?
  // Make two separate components?
  const handleChange = (
    newLevels:
      | CoreSkillLevel
      | CoreSkillLevel[]
      | DigitalSkillLevel
      | DigitalSkillLevel[]
      | SculptureSkillLevel
      | SculptureSkillLevel[]
  ) => {
    if (typeof newLevels === "string") {
      setSkillLevels(newLevels)
      onChange?.(newLevels)
      return
    }

    const sortedLevels = sortSkillLevels(newLevels, classGroup)
    setSkillLevels(sortedLevels)
    onChange?.(sortedLevels)
  }

  return (
    <Form.Item
      name={fieldName ?? `skillLevel${multiSelect ? "s" : ""}`}
      label={
        label ??
        `${camelToWords(classGroup)} skill level${multiSelect ? "s" : ""}`
      }
      rules={[
        ...(required
          ? [
              {
                required: true,
                message: "Please select skill level!",
              },
            ]
          : []),
        ({ getFieldValue }) => ({
          async validator(
            _,
            value:
              | CoreSkillLevel
              | CoreSkillLevel[]
              | DigitalSkillLevel
              | DigitalSkillLevel[]
              | SculptureSkillLevel
              | SculptureSkillLevel[]
              | undefined
          ) {
            const selectedGroup = classGroup ?? getFieldValue("group")
            if (!selectedGroup && requireGroupSelection) {
              return Promise.reject(
                new Error("Please select a class group first")
              )
            }
            // Convert everything to array for easier handling
            const newValues = typeof value === "string" ? [value] : value

            const typedValues =
              selectedGroup === CLASS_GROUPS.DIGITAL_ART
                ? (newValues as DigitalSkillLevel[])
                : selectedGroup === CLASS_GROUPS.SCULPTURE
                  ? (newValues as SculptureSkillLevel[])
                  : (newValues as CoreSkillLevel[])

            if (!typedValues?.length) return Promise.resolve()

            const expectedLevels =
              selectedGroup === CLASS_GROUPS.DIGITAL_ART
                ? DIGITAL_SKILL_LEVELS
                : selectedGroup === CLASS_GROUPS.SCULPTURE
                  ? SCULPTURE_SKILL_LEVELS
                  : CORE_SKILL_LEVELS

            const isAllCorrectSkillLevels = typedValues.every(
              (level) => expectedLevels[level as keyof typeof expectedLevels]
            )

            if (!isAllCorrectSkillLevels) {
              return Promise.reject(
                new Error("Please match the skill levels to the class group")
              )
            }

            return Promise.resolve()
          },
        }),
      ]}
    >
      <Select
        mode={multiSelect ? "multiple" : undefined}
        value={skillLevels}
        onChange={handleChange}
        options={skillLevelOptions}
        placeholder={`Select skill level${multiSelect ? "s" : ""}`}
        showSearch
        filterOption={filterFormOptions}
        allowClear
      />
    </Form.Item>
  )
}

export default SkillLevelDropdown
